/**
 * Difficulty grading -- by the technique a puzzle actually requires, never by
 * clue count.
 *
 * Clue count is the lazy proxy the mass-market apps use, and mislabelled
 * "Expert" is the single most-mocked failing in this category. Here a tier is a
 * statement with evidence behind it: the solver logged every deduction, and the
 * tier is the tier of the hardest one it needed. `techniques` is that evidence,
 * and the UI shows it to the player.
 */

import type { Board } from "./grid";
import type { ConstraintSet } from "./constraints";
import { solveHuman } from "./solver-human";
import { TECHNIQUE_BY_ID, TECHNIQUES, TIER_ORDER } from "./techniques";
import type { TechniqueId, Tier } from "./techniques/types";
import { generate, type GenerateOptions, type GeneratedPuzzle } from "./generate";

/** A puzzle needing a technique we have not implemented yet (chains, coloring). */
export type Rating = Tier | "unrated";

export interface Grade {
  readonly tier: Rating;
  /** Hardest single step, on the Sudoku Explainer scale. 0 for an empty solve. */
  readonly ser: number;
  /** Distinct techniques required, easiest first. */
  readonly techniques: readonly TechniqueId[];
  readonly steps: number;
  readonly solved: boolean;
}

export function grade(board: Board, options: { cs?: ConstraintSet } = {}): Grade {
  const result = solveHuman(board, { cs: options.cs });

  const used = new Set<TechniqueId>();
  let ser = 0;
  let hardest = -1;

  for (const step of result.steps) {
    used.add(step.technique);
    const technique = TECHNIQUE_BY_ID.get(step.technique)!;
    ser = Math.max(ser, technique.ser);
    hardest = Math.max(hardest, TIER_ORDER.indexOf(technique.tier));
  }

  const techniques = TECHNIQUES.filter((technique) => used.has(technique.id)).map((t) => t.id);

  return {
    // An unfinished solve is honestly unrated rather than optimistically "Evil":
    // we know it is at least Expert, but not what it actually needs.
    tier: result.solved ? (TIER_ORDER[Math.max(hardest, 0)] as Tier) : "unrated",
    ser,
    techniques,
    steps: result.steps.length,
    solved: result.solved,
  };
}

export interface GradedPuzzle extends GeneratedPuzzle {
  readonly grade: Grade;
}

/**
 * Clue counts to start digging from, per tier.
 *
 * Purely a search accelerator: unguided digging lands around 24 clues, which
 * skews hard, so easy tiers would otherwise be rejected hundreds of times. The
 * grade is still what decides the tier -- this only biases which puzzles get
 * offered to the grader.
 */
const MIN_CLUES: Record<Tier, number> = {
  easy: 34,
  medium: 30,
  hard: 26,
  expert: 24,
  evil: 22,
};

export interface GenerateGradedOptions extends GenerateOptions {
  /** Give up after this many candidates. Default 200. */
  maxAttempts?: number;
}

/**
 * Generate until a puzzle grades into `tier`.
 *
 * Returns null if it cannot find one within `maxAttempts` -- which is the
 * honest outcome for "evil" today, since chains are not implemented and no
 * puzzle can currently be shown to require them.
 */
export function generateGraded(
  tier: Tier,
  options: GenerateGradedOptions = {},
): GradedPuzzle | null {
  const maxAttempts = options.maxAttempts ?? 200;
  const baseSeed = options.seed;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const puzzle = generate({
      ...options,
      seed: baseSeed === undefined ? undefined : baseSeed + attempt,
      minClues: options.minClues ?? MIN_CLUES[tier],
    });
    const result = grade(puzzle.puzzle, { cs: options.cs });
    if (result.tier === tier) return { ...puzzle, grade: result };
  }
  return null;
}
